import { config } from "../../config/config";
import { authHeaders, handleV2Error } from "./_helpers";
import { accessEntriesV2ResponseSchema } from "../../utils/zod/accessControl";
import type { AccessStatus } from "../../utils/zod/accessControl";
import type { RegisterAccessResponse } from "../../utils/zod/accessControl";
import type { ActiveClientsResponse } from "../../utils/zod/accessControl";
import type { ClientAccessLogsResponse } from "../../utils/zod/accessControl";
import type { FaceDescriptorsResponse } from "../../utils/zod/accessControl";

export const searchClientsForAccessRequest = async (
  query: string,
  token: string,
): Promise<AccessStatus[]> => {
  const url = `${config.API_URL}/access-control/search?q=${encodeURIComponent(query)}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al buscar clientes para control de acceso",
    );
  }

  return response.json();
};

export const registerAccessRequest = async (
  clientPublicId: string | undefined,
  token: string,
  reason?: string,
  validationMethod?: string,
  confidenceScore?: number,
  accessTimestamp?: string,
  guestName?: string,
): Promise<RegisterAccessResponse> => {
  const body: Record<string, unknown> = {};
  if (clientPublicId) {
    body.clientPublicId = clientPublicId;
  }
  if (guestName?.trim()) {
    body.guestName = guestName.trim();
  }
  if (reason != null && reason.trim() !== "") {
    body.reason = reason.trim();
  }
  if (validationMethod) {
    body.validationMethod = validationMethod;
  }
  if (confidenceScore != null) {
    body.confidenceScore = confidenceScore;
  }
  if (accessTimestamp) {
    body.accessTimestamp = accessTimestamp;
  }
  const response = await fetch(`${config.API_URL}/access-control`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Error al registrar acceso");
  }

  return response.json();
};

export const getActiveClientsRequest = async (
  token: string,
  page = 1,
  date?: string,
): Promise<ActiveClientsResponse> => {
  const limit = 10;
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (date) params.set("date", date);

  const response = await fetch(
    `${config.API_V2_URL}/v2/access-control/entries?${params.toString()}`,
    { method: "GET", headers: authHeaders(token) },
  );

  if (!response.ok) await handleV2Error(response);

  const body = await response.json();
  const { data, pagination } = accessEntriesV2ResponseSchema.parse(body);

  return {
    clients: data,
    total: pagination.totalCount,
    limit: pagination.perPage,
    offset: (page - 1) * limit,
  };
};

export const getClientAccessLogsRequest = async (
  clientPublicId: string,
  token: string,
  page = 1,
  limit = 20,
): Promise<ClientAccessLogsResponse> => {
  const offset = (page - 1) * limit;
  const response = await fetch(
    `${config.API_URL}/access-control/client/${clientPublicId}?limit=${limit}&offset=${offset}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al obtener el historial de accesos",
    );
  }

  return response.json();
};

export interface AccessExceptionEntry {
  publicId: string;
  clientName: string;
  reason: string;
  authorizedBy: string;
  createdAt: string;
}

export interface AccessExceptionsResponse {
  exceptions: AccessExceptionEntry[];
}

export const getAccessExceptionsRequest = async (
  token: string,
  limit?: number,
): Promise<AccessExceptionsResponse> => {
  const params = new URLSearchParams();
  if (limit != null && limit > 0) params.set("limit", String(limit));
  const query = params.toString();
  const url = `${config.API_URL}/access-control/exceptions${query ? `?${query}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al obtener ingresos excepcionales",
    );
  }

  return response.json();
};

export const getClientStatusRequest = async (
  clientPublicId: string,
  token: string,
): Promise<AccessStatus> => {
  const response = await fetch(
    `${config.API_URL}/access-control/client-status/${clientPublicId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Error al obtener estado del cliente");
  }

  return response.json();
};

export const getFaceDescriptorsRequest = async (
  token: string,
  updatedAfter?: string,
): Promise<FaceDescriptorsResponse> => {
  const params = new URLSearchParams();
  if (updatedAfter) params.set("updatedAfter", updatedAfter);
  const query = params.toString();
  const url = `${config.API_URL}/access-control/face-descriptors${query ? `?${query}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || "Error al obtener descriptores faciales",
    );
  }

  return response.json();
};

export const storeFaceDescriptorRequest = async (
  clientPublicId: string,
  descriptor: number[],
  sourceKey: string,
  token: string,
): Promise<{ publicId: string }> => {
  const response = await fetch(
    `${config.API_URL}/access-control/face-descriptors/${clientPublicId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ descriptor, sourceKey }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Error al guardar descriptor facial");
  }

  return response.json();
};
